import { useRef, useEffect, useCallback, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { PopupLayer } from './PopupLayer'
import { useDialogLayers } from './popupLayers'
import type {
  CSSProperties,
  FormEvent,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  ReactNode,
  Ref,
} from 'react'
import clsx from 'clsx'
import { CloseIcon, CollapseIcon, ExpandIcon, PopUpIcon, SidePanelIcon } from '../icons/icons'
import { IconButton } from './IconButton'
import { DockedModalPanel } from './DockedModalPanel'
import { ResizeGlyph } from './ResizeGlyph'
import { WithoutPageTrailSlot } from './PageTrailSlotProvider'
import TitlebarSlotProvider from './TitlebarSlotProvider'
import { focusQuietly } from '../utils/quietFocus'
import { consumeNextClick } from '../utils/consumeNextClick'

import { FormRequirements } from './FormRequirements'
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'

function focusablesIn(root: Element | null): HTMLElement[] {
  if (!root) return []
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => !element.closest('[hidden], [aria-hidden="true"], [inert]')
  )
}

/**
 * Open dialogs, innermost last: only the innermost keeps focus and answers
 * Escape and clicks outside.
 */
const openShells: HTMLElement[] = []

function isInnermostShell(shell: HTMLElement | null) {
  // Not registered yet (the open effect hasn't run): it's the newest.
  return !shell || !openShells.includes(shell) || openShells[openShells.length - 1] === shell
}

const WIDTH_CLASSES = {
  sm: 'w-[320px]',
  md: 'w-[480px]',
  lg: 'w-[640px]',
  xl: 'w-[800px]',
  fit: 'w-fit',
  auto: 'w-auto',
} as const

/** A dialog that can be pinned beside the page; see ModalShell's `dock`. */
export interface ModalShellDock {
  docked: boolean
  onDock: () => void
  /** Back to a pop-up, still open, its draft kept. */
  onUndock?: () => void
  /** The view it's pinned beside, which changes when the person switches views or tables. */
  viewKey?: string | null
  /** Its width the first time it's pinned (DockedModalPanel's default otherwise). */
  defaultWidth?: number
}

export type ModalShellResizeCorner = 'left' | 'right'

/**
 * Grips at the bottom corners of a floating dialog. The dialog owns its size
 * (it passes it back through `style`), so each press is the dialog's to handle.
 */
export interface ModalShellResizeGrips {
  onMouseDown: (event: ReactMouseEvent<HTMLDivElement>, corner: ModalShellResizeCorner) => void
  /** The grip's cursor, for the directions the dialog can still grow in. */
  cursor?: (corner: ModalShellResizeCorner) => string
  onMouseEnter?: (corner: ModalShellResizeCorner) => void
}

interface ModalShellProps {
  title: string
  /** Above the title, what kind of thing this is ("Field Settings", "Table Settings"). */
  eyebrow?: string
  description?: string | null
  /**
   * Under the title, the dialog's tabs (a TabMenu). A dialog with an eyebrow or
   * tabs is a settings card: its header packs tighter, the tabs on its bottom
   * edge, and its content brings its own padding (`contentClassName`).
   */
  tabs?: ReactNode
  open: boolean
  onClose: () => void
  /** First in the header: how the panel works (Groups' Advanced switch). */
  headerMode?: ReactNode
  /** Then its actions and options (copy from another view, the ⋮ menu). */
  headerActions?: ReactNode
  /**
   * Then the window's own controls, which the shell draws: side panel or
   * pop-up (with `dock`) and, here, Expand / Restore size. Then ✕. Dividers
   * separate the groups that have something in them.
   */
  expandable?: boolean
  /**
   * Controls Expand from outside, for a dialog that lays itself out differently
   * expanded (Field Settings gives its text boxes the room). Uncontrolled
   * without it.
   */
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  /** Falsy children omit the content region entirely (header/footer-only dialogs). */
  children?: ReactNode
  footer?: ReactNode
  /**
   * A bar under the content and footer that takes its own room, such as a
   * SaveChangesBar: the content scrolls above it, never behind it.
   */
  statusBar?: ReactNode
  width?: keyof typeof WIDTH_CLASSES
  className?: string
  contentClassName?: string
  contentScrollable?: boolean
  style?: CSSProperties
  /** A floating dialog in the middle of the window (Add field, Add Table) rather than where `style` puts it. */
  centered?: boolean
  /**
   * Whether it's modal (the default): focus stays inside on Tab, and it says
   * so to assistive tech. A card that opens beside what it edits (Field
   * Settings under a column header) is a dialog that isn't.
   */
  modal?: boolean
  /**
   * The dialog is a form: header, content and footer sit in one `<form>`, so
   * a submit button in the footer submits it, floating or docked.
   */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void
  /** Corner grips for a dialog that can be resized; see ModalShellResizeGrips. */
  resizeGrips?: ModalShellResizeGrips
  /** The scrolling content element, for a dialog that measures it. */
  contentRef?: Ref<HTMLDivElement>
  dataScope?: string
  shellRef?: MutableRefObject<HTMLDivElement | null> | ((node: HTMLDivElement | null) => void)
  backdrop?: boolean
  /**
   * Whether a click outside or Escape closes it at all. A dialog holding edits
   * stays dismissible and asks first instead: pass useDismissGuard's
   * requestClose as onClose and render UnsavedChangesQuestion beside it.
   */
  dismissible?: boolean
  /**
   * Lets it open as a side card beside the page, and back as a pop-up, from
   * a button in its header. As a side card the page stays usable and its
   * edits stay a draft; switching either way keeps the draft. ✕ closes it,
   * asking first about unsaved changes.
   */
  dock?: ModalShellDock
}

const HEADER_ICON_BUTTON_CLASS =
  'rounded-md p-1 text-icon-quiet transition-colors hover:bg-muted hover:text-data'

export function ModalShell({
  title,
  eyebrow,
  description,
  tabs,
  open,
  onClose,
  headerMode,
  headerActions,
  expandable = false,
  expanded: expandedProp,
  onExpandedChange,
  children,
  footer,
  statusBar,
  width = 'md',
  className,
  contentClassName,
  contentScrollable = true,
  style,
  centered = false,
  modal = true,
  onSubmit,
  resizeGrips,
  contentRef: contentElementRef,
  dataScope,
  shellRef,
  dismissible = true,
  backdrop = false,
  dock,
}: ModalShellProps) {
  const docked = Boolean(dock?.docked)
  // Floating, it's a dialog: focus, Escape and clicks outside are its own.
  const floating = open && !docked
  const settingsCard = Boolean(eyebrow || tabs)
  // Expanded, a pop-up fills the window but for a margin; it opens normal.
  // A side panel expands as a pop-up, and restoring puts it back beside the
  // page; either way its draft stays.
  const [expandedState, setExpandedState] = useState(false)
  const expanded = expandedProp ?? expandedState
  const setExpanded = (next: boolean) => {
    if (expandedProp === undefined) setExpandedState(next)
    onExpandedChange?.(next)
  }
  const [expandedFromSidePanel, setExpandedFromSidePanel] = useState(false)
  useEffect(() => {
    if (open) return
    setExpandedState(false)
    setExpandedFromSidePanel(false)
  }, [open])
  const isExpanded = expanded && expandable && !docked
  const canExpand = expandable && (!docked || Boolean(dock?.onUndock))
  const toggleExpanded = () => {
    if (isExpanded) {
      setExpanded(false)
      if (expandedFromSidePanel) {
        setExpandedFromSidePanel(false)
        dock?.onDock()
      }
      return
    }
    if (docked) {
      setExpandedFromSidePanel(true)
      dock?.onUndock?.()
    }
    setExpanded(true)
  }
  const openAsSidePanel = () => {
    setExpanded(false)
    setExpandedFromSidePanel(false)
    dock?.onDock()
  }
  const dialogLayers = useDialogLayers()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const hasDescription = Boolean(description)
  const titleId = useId()
  const descriptionId = useId()

  const setContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node

      if (!shellRef) return
      if (typeof shellRef === 'function') {
        shellRef(node)
        return
      }
      shellRef.current = node
    },
    [shellRef]
  )

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node
      if (!dismissible || !isInnermostShell(contentRef.current)) return
      // By the event's path, not `contains`: a press can re-render its target
      // away (an autocomplete option) before this listener runs.
      const inside = Boolean(contentRef.current && e.composedPath().includes(contentRef.current))
      if (contentRef.current && !inside) {
        // Don't close if the click landed inside a child portal (e.g. ContextMenu popover)
        const clickedPortal = (target as Element).closest?.('[role="dialog"], [data-portal]')
        if (clickedPortal) return
        onClose()
        // A dialog that asked about unsaved changes instead of closing keeps
        // this press's click from also acting on what's behind it.
        consumeNextClick(() => Boolean(contentRef.current?.isConnected))
      }
    },
    [dismissible, onClose]
  )

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (!dismissible || e.key !== 'Escape' || e.defaultPrevented) return
      // A dialog opened over this one (such as its unsaved-changes question) takes it.
      if (!isInnermostShell(contentRef.current)) return
      // So does a modal dialog that isn't a ModalShell (a page's own question).
      const shell = contentRef.current
      const otherModal = [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].some(
        (dialog) =>
          dialog instanceof HTMLElement &&
          !openShells.includes(dialog) &&
          !shell?.contains(dialog) &&
          !dialog.contains(shell)
      )
      if (otherModal) return
      // A popover open in the dialog takes this Escape; the next one is the dialog's.
      if (document.querySelector('[data-escape-consumer="popover"]')) return
      // Flag it so later listeners (side panels, fullscreen fallbacks) don't also act on it.
      e.preventDefault()
      onClose()
    },
    [dismissible, onClose]
  )

  useEffect(() => {
    if (floating) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscape)
      }
    }
  }, [floating, handleClickOutside, handleEscape])

  // Focus moves in on open, Tab cycles inside, and focus goes back on close.
  useEffect(() => {
    const shell = contentRef.current
    if (!floating || !shell) return
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    openShells.push(shell)
    // A control that took focus itself (autoFocus) keeps it.
    if (!shell.contains(document.activeElement)) {
      const start =
        focusablesIn(shell.querySelector('[data-modal-content]'))[0] ??
        focusablesIn(shell.querySelector('[data-modal-footer]'))[0] ??
        shell
      // Quietly: a control's tooltip shouldn't pop up just because the dialog opened.
      focusQuietly(start, { preventScroll: true })
    }
    const keepFocusInside = (event: KeyboardEvent) => {
      if (!modal || event.key !== 'Tab' || openShells[openShells.length - 1] !== shell) return
      const active = document.activeElement
      // A popover the dialog opened (a dropdown's list) handles its own keys.
      if (active && !shell.contains(active) && active.closest('[data-portal], [role="listbox"]')) {
        return
      }
      const items = focusablesIn(shell)
      if (items.length === 0) {
        event.preventDefault()
        shell.focus({ preventScroll: true })
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (!active || !shell.contains(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      } else if (event.shiftKey && (active === first || active === shell)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', keepFocusInside)
    return () => {
      document.removeEventListener('keydown', keepFocusInside)
      const index = openShells.indexOf(shell)
      if (index !== -1) openShells.splice(index, 1)
      // Back to what opened it, when that is still on the page.
      if (previous && previous !== document.body && previous.isConnected) {
        focusQuietly(previous, { preventScroll: true })
      }
    }
  }, [floating, modal])

  if (!open) return null

  const header = (
    <div
      className={clsx(
        'flex flex-none justify-between border-b',
        // A settings card's tabs sit on its bottom edge.
        settingsCard ? 'border-muted px-3 pt-3' : 'border-default px-4 py-3',
        hasDescription || settingsCard ? 'items-start' : 'items-center',
        // Pinned narrow, the buttons drop below the title rather than squeeze it.
        docked && 'flex-wrap gap-y-2 rounded-t-[inherit]'
      )}
    >
      <div className={clsx('min-w-0 flex-1', docked && 'min-w-[6rem]')}>
        {eyebrow ? <p className="text-xs leading-3 text-muted">{eyebrow}</p> : null}
        <h2
          id={titleId}
          className={clsx(
            'truncate text-sm font-semibold text-default',
            eyebrow && 'mt-0.5 leading-[18px]'
          )}
        >
          {title}
        </h2>
        {hasDescription && (
          <p id={descriptionId} className="mt-0.5 text-xs text-muted">
            {description}
          </p>
        )}
        {tabs ? <div className="mt-1">{tabs}</div> : null}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-1 pl-3">
        {/* How it works │ its actions │ the window │ close. */}
        {[
          headerMode,
          headerActions,
          dock || canExpand ? (
            <>
              {dock && (dock.docked ? dock.onUndock : true) ? (
                <IconButton
                  label={docked ? 'Open as pop-up' : 'Open as side panel'}
                  onClick={docked ? dock.onUndock : openAsSidePanel}
                  className={HEADER_ICON_BUTTON_CLASS}
                >
                  {docked ? <PopUpIcon size={16} /> : <SidePanelIcon size={16} />}
                </IconButton>
              ) : null}
              {canExpand ? (
                <IconButton
                  label={isExpanded ? 'Restore size' : 'Expand'}
                  onClick={toggleExpanded}
                  className={HEADER_ICON_BUTTON_CLASS}
                >
                  {isExpanded ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
                </IconButton>
              ) : null}
            </>
          ) : null,
        ]
          .filter(Boolean)
          .map((cluster, index) => (
            <span key={index} className="flex items-center gap-1">
              {cluster}
              <span aria-hidden className="mx-1 h-4 w-px bg-[color:var(--border-default)]" />
            </span>
          ))}
        <button
          type="button"
          onClick={onClose}
          className={HEADER_ICON_BUTTON_CLASS}
          aria-label="Close"
        >
          <CloseIcon size={16} />
        </button>
      </div>
    </div>
  )

  if (docked) {
    return (
      <WithoutPageTrailSlot>
        <TitlebarSlotProvider>
          <DockedModalPanel
            title={title}
            widthKey={eyebrow}
            titleId={titleId}
            onClose={onClose}
            header={header}
            footer={footer}
            footerClassName={settingsCard ? 'border-muted p-3' : undefined}
            contentClassName={settingsCard ? contentClassName : undefined}
            contentRef={contentElementRef}
            defaultWidth={dock?.defaultWidth}
            onSubmit={onSubmit}
          >
            {children ? <FormRequirements>{children}</FormRequirements> : null}
          </DockedModalPanel>
        </TitlebarSlotProvider>
      </WithoutPageTrailSlot>
    )
  }

  const body = (
    <>
      {header}

      {/* Content */}
      {children ? (
        <div
          ref={contentElementRef}
          data-modal-content
          className={clsx(
            'min-h-0',
            // A settings card's content brings its own padding.
            !settingsCard && 'px-4 py-3',
            contentScrollable ? 'overflow-y-auto' : 'overflow-visible',
            isExpanded && 'flex-1',
            contentClassName
          )}
        >
          {/* A dialog is a form: its fields' requirement marks are decided together. */}
          <FormRequirements>{children}</FormRequirements>
        </div>
      ) : null}

      {/* Footer */}
      {footer && (
        <div
          data-modal-footer
          className={clsx(
            'flex-none border-t',
            settingsCard ? 'border-muted p-3' : 'border-default px-4 py-3'
          )}
        >
          {footer}
        </div>
      )}

      {statusBar ? <div className="flex flex-none flex-col">{statusBar}</div> : null}
    </>
  )
  const showGrips = Boolean(resizeGrips) && !isExpanded

  return createPortal(
    // A page header inside a dialog keeps its trail in its own row, not the app's top bar,
    // and pairs with a grid in the dialog, never with the page's grid behind it.
    <PopupLayer layer={dialogLayers.menu}>
      <WithoutPageTrailSlot>
        <TitlebarSlotProvider>
          {/* Expanded, it covers the page, so the page dims behind it as the
              field settings dialog's does. */}
          {(backdrop || isExpanded) && (
            <div
              aria-hidden="true"
              className="fixed inset-0 bg-[color:var(--bg-overlay)]"
              style={{ zIndex: dialogLayers.overlay }}
            />
          )}
          <div
            ref={setContentRef}
            role="dialog"
            aria-modal={modal ? 'true' : 'false'}
            aria-labelledby={titleId}
            aria-describedby={hasDescription ? descriptionId : undefined}
            tabIndex={-1}
            data-modal-scope={dataScope}
            data-escape-consumer="modal"
            className={clsx(
              'fixed flex max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-default surface-default shadow-lg outline-none',
              // Expanded: the window but for a 24px margin, wherever it hung.
              isExpanded
                ? 'inset-6'
                : [
                    WIDTH_CLASSES[width],
                    centered && 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                    className,
                  ]
            )}
            style={
              isExpanded
                ? { zIndex: dialogLayers.dialog }
                : { ...style, zIndex: dialogLayers.dialog }
            }
          >
            {onSubmit ? (
              // `contents`, so header, content and footer stay the dialog's flex column.
              <form className="contents" onSubmit={onSubmit}>
                {body}
              </form>
            ) : (
              body
            )}
            {showGrips && resizeGrips
              ? (['left', 'right'] as const).map((corner) => (
                  <div
                    key={corner}
                    aria-hidden="true"
                    data-resize-grip={corner}
                    className={clsx(
                      'absolute bottom-0 h-4 w-4',
                      corner === 'left' ? 'left-0 rounded-bl-lg' : 'right-0 rounded-br-lg'
                    )}
                    style={{ cursor: resizeGrips.cursor?.(corner) }}
                    onMouseEnter={() => resizeGrips.onMouseEnter?.(corner)}
                    onMouseDown={(event) => resizeGrips.onMouseDown(event, corner)}
                  >
                    <ResizeGlyph corner={corner} />
                  </div>
                ))
              : null}
          </div>
        </TitlebarSlotProvider>
      </WithoutPageTrailSlot>
    </PopupLayer>,
    document.body
  )
}
